import { fetchBaseQuery } from '@reduxjs/toolkit/query';
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError
} from '@reduxjs/toolkit/query';
import Jsona from 'jsona';
import camelcaseKeys from 'camelcase-keys-deep';
import { API_URL } from '@/configs/dybr';

const jsona = new Jsona();

/**
 * Custom base query for RTK Query that:
 * 1. Automatically adds Authorization Bearer token from localStorage
 * 2. Deserializes JSON:API responses using jsona
 * 3. Transforms response keys to camelCase
 */
export const baseQueryWithAuth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const token = localStorage.getItem('token')?.replace(/"/g, '') ?? '';

  const baseQuery = fetchBaseQuery({
    baseUrl: API_URL,
    prepareHeaders: headers => {
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    }
  });

  const result = await baseQuery(args, api, extraOptions);

  if (result.data && !result.error) {
    try {
      // Deserialize JSON:API response using jsona
      const camelCasedData = camelcaseKeys(result.data);
      const deserialized = jsona.deserialize(camelCasedData);
      const camelCasedDeserialized = camelcaseKeys(deserialized);

      // Attach meta as a property on the data
      const meta = (result.data as any).meta
        ? camelcaseKeys((result.data as any).meta)
        : {};

      // Add meta property to the deserialized data
      if (Array.isArray(camelCasedDeserialized)) {
        Object.defineProperty(camelCasedDeserialized, 'meta', {
          value: meta,
          enumerable: false,
          writable: false
        });
      } else if (typeof camelCasedDeserialized === 'object') {
        (camelCasedDeserialized as any).meta = meta;
      }

      return {
        data: camelCasedDeserialized
      };
    } catch (error) {
      console.warn('Failed to deserialize response:', error);
      return result;
    }
  }

  return result;
};
